<script setup>
import { cloneDeep } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { computed, reactive } from 'vue';
import { useAuthStore } from '~/auth/stores/auth.store';
import BiQueryBuilderColumnPicker from '~/bi/components/query-builder/column-picker/bi-query-builder-column-picker.vue';
import BiQueryBuilderTablePicker from '~/bi/components/query-builder/table-picker/bi-query-builder-table-picker.vue';
import { useBIDatasetPicker } from '~/bi/composables/useBIDatasetPicker';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useCommonStore } from '~/common/stores/common.store';
import { stringToNumber } from '~/common/utils/common.utils';

const emit = defineEmits(['close', 'save']);

const { $t, $toast, $services, route, router } = useCommonImports();
const auth_store = useAuthStore();
const common_store = useCommonStore();
const bi_store = useBiStore();

const { selected_dashboard_details } = storeToRefs(bi_store);

const bi_query_builder = useBIQueryBuilder();

const state = reactive({
  form_data: {},
  is_loading: false,
  target_schemas: [],
  target_schemas_by_name: {},
  table_mappings: {},
  field_mappings: {},
  used_tables: [],
  is_fetching_schemas: false,
});

const { hierarchicalSchemas: targetHierarchicalSchemas } = useBIDatasetPicker(computed(() => state.target_schemas));

const bi_enabled_assets = computed(() => {
  return common_store.assets.filter((asset) => {
    return auth_store.has_bi_access && auth_store.has_dw_enabled(asset.uid);
  });
});

const is_mapping_complete = computed(() => {
  const are_all_tables_mapped = state.used_tables.every((table) => {
    return state.table_mappings[table.name];
  });

  if (!are_all_tables_mapped)
    return false;

  const are_all_fields_mapped = state.used_tables.every((table) => {
    const target_table = state.table_mappings[table.name];
    if (!target_table)
      return false;

    return table.fields.every((field) => {
      return state.field_mappings[table.name]?.[field];
    });
  });

  return are_all_fields_mapped;
});

const is_standard_dashboard = computed(() => selected_dashboard_details.value?.standard === true);

const is_save_disabled = computed(() => {
  if (!state.form_data.name)
    return true;
  if (is_standard_dashboard.value && state.form_data.selected_asset && state.form_data.selected_asset.uid !== route.params.asset_id)
    return true;
  if (state.form_data.selected_asset && state.form_data.selected_asset.uid !== route.params.asset_id)
    return !is_mapping_complete.value;
  return false;
});

// Extract all unique tables and fields used in the dashboard
function extractTablesAndFields() {
  const tables_map = {};
  const generated_series_tables = new Set();

  const addTable = (table_name, field_name = null) => {
    if (!table_name || table_name === 'results')
      return;
    if (generated_series_tables.has(table_name))
      return;
    if (!tables_map[table_name]) {
      const schema_info = bi_store.all_schema_by_name?.[table_name];
      tables_map[table_name] = {
        name: table_name,
        label: schema_info?.label || table_name,
        path: schema_info?.path || [],
        fields: new Set(), // contains the actual name that is mapped
        field_details: {}, // contains the details like label which can be used for display purposes
      };
    }
    if (field_name) {
      const schema_info = bi_store.all_schema_by_name?.[table_name];
      const field_detail = schema_info?.columns?.find(col => col.name === field_name || col.label === field_name);

      if (field_detail) {
        tables_map[table_name].fields.add(field_detail.name);
        tables_map[table_name].field_details[field_detail.name] = field_detail;
      }
      else {
        tables_map[table_name].fields.add(field_name);
        tables_map[table_name].field_details[field_name] = { name: field_name, label: field_name, path: [] };
      }
    }
  };

  const extractFieldInfo = (field_ref) => {
    if (!field_ref)
      return null;
    const parts = field_ref.split('→');
    if (parts.length === 2) {
      return { table: parts[0], field: parts[1] };
    }
    return null;
  };

  // Extract field references from expression strings
  // Expressions contain field references in square brackets like [table_name→field_name]
  const extractFieldsFromExpression = (expr) => {
    if (!expr || typeof expr !== 'string')
      return [];

    // Match all field references in square brackets: [table_name→field_name]
    // The arrow character is → (U+2192)
    const field_regex = /\[([^[\]][^[\]\u2192]*\u2192[^[\]]+)\]/g;
    const matches = [];
    let match;

    // eslint-disable-next-line no-cond-assign
    while ((match = field_regex.exec(expr)) !== null) {
      matches.push(match[1]); // Extract the content inside brackets
    }

    return matches;
  };

  const widgets = Array.isArray(selected_dashboard_details.value.widgets) ? selected_dashboard_details.value.widgets : [];
  widgets.forEach((widget) => {
    const stages = widget.config?.query?.stages || [];

    stages.forEach((stage) => {
      if (stage.table?.generateSeries) {
        const table_name = stage.table.name || stage.table.alias;
        if (table_name)
          generated_series_tables.add(table_name);
      }
      else if (stage.table?.name) {
        addTable(stage.table.name);
      }
      else if (stage.table && typeof stage.table === 'string') {
        addTable(stage.table);
      }

      stage.joins?.forEach((join) => {
        if (join.table?.generateSeries) {
          const table_name = join.table.name || join.table.alias;
          if (table_name)
            generated_series_tables.add(table_name);
        }
        else if (join.table?.name) {
          addTable(join.table.name);
        }

        join.on?.forEach((condition) => {
          const leftInfo = extractFieldInfo(condition.left?.field || condition.left);
          const rightInfo = extractFieldInfo(condition.right?.field || condition.right);
          if (leftInfo)
            addTable(leftInfo.table, leftInfo.field);
          if (rightInfo)
            addTable(rightInfo.table, rightInfo.field);
        });

        if (join.condition) {
          const field_refs = extractFieldsFromExpression(join.condition);
          field_refs.forEach((field_ref) => {
            const fieldInfo = extractFieldInfo(field_ref);
            if (fieldInfo)
              addTable(fieldInfo.table, fieldInfo.field);
          });
        }
      });

      stage.columns?.forEach((column) => {
        if (column.table_name) {
          addTable(column.table_name, column.label);
        }
        if (column.field) {
          const fieldInfo = extractFieldInfo(column.field);
          if (fieldInfo)
            addTable(fieldInfo.table, fieldInfo.field);
        }

        if (column.expr) {
          const field_refs = extractFieldsFromExpression(column.expr);
          field_refs.forEach((field_ref) => {
            const fieldInfo = extractFieldInfo(field_ref);
            if (fieldInfo)
              addTable(fieldInfo.table, fieldInfo.field);
          });
        }
      });

      stage.filters?.forEach((filter) => {
        if (filter.table_name) {
          addTable(filter.table_name, filter.name);
        }

        if (filter.field) {
          const fieldInfo = extractFieldInfo(filter.field);
          if (fieldInfo)
            addTable(fieldInfo.table, fieldInfo.field);
        }

        if (filter.expr) {
          const field_refs = extractFieldsFromExpression(filter.expr);
          field_refs.forEach((field_ref) => {
            const fieldInfo = extractFieldInfo(field_ref);
            if (fieldInfo)
              addTable(fieldInfo.table, fieldInfo.field);
          });
        }
      });
    });
  });

  selected_dashboard_details.value.meta?.global_filters?.forEach((filter) => {
    if (filter.general?.dataset) {
      addTable(filter.general.dataset, filter.general.column_name);
    }
  });

  state.used_tables = Object.values(tables_map).map((table) => {
    return {
      ...table,
      fields: Array.from(table.fields),
      field_details: table.field_details,
    };
  });
}

async function fetchTargetSchemas() {
  if (!state.form_data.selected_asset)
    return;

  state.is_fetching_schemas = true;
  try {
    const connection_id = `${auth_store.current_organization.uid}__${state.form_data.selected_asset.uid}`;
    const response = await $services.bi_schema.table({ id: connection_id });

    state.target_schemas = response.data.tables.map(table => ({
      label: table.label || table.name,
      name: table.name,
      path: table.path,
      columns: table.columns.map(column => ({
        ...column,
        table_name: table.name,
        label: column.label || column.name,
        name: column.name,
      })),
    }));

    state.target_schemas_by_name = state.target_schemas.reduce((acc, table) => {
      acc[table.name] = table;
      return acc;
    }, {});

    autoFillTableMappings();
  }
  catch (error) {
    logger.error('Failed to fetch target schemas:', error);
    $toast({
      title: $t('Error'),
      text: $t('Failed to fetch datasets for the selected asset'),
      type: 'error',
    });
  }
  finally {
    state.is_fetching_schemas = false;
  }
}

function autoFillTableMappings() {
  state.used_tables.forEach((sourceTable) => {
    // Try to find a table with the same path and label
    const matchingTable = state.target_schemas.find((targetTable) => {
      return JSON.stringify(targetTable.path) === JSON.stringify(sourceTable.path) && targetTable.label === sourceTable.label;
    });

    if (matchingTable) {
      state.table_mappings[sourceTable.name] = matchingTable;
      autoFillFieldMappings(sourceTable.name);
    }
    else {
      state.table_mappings[sourceTable.name] = null;
      state.field_mappings[sourceTable.name] = {};
    }
  });
}

function autoFillFieldMappings(sourceTableName) {
  const targetTable = state.table_mappings[sourceTableName];
  if (!targetTable)
    return;

  const sourceTable = state.used_tables.find(t => t.name === sourceTableName);
  if (!sourceTable)
    return;

  state.field_mappings[sourceTableName] = {};

  sourceTable.fields.forEach((sourceField) => {
    // Try to find a field with the same name
    const matchingField = targetTable.columns?.find((col) => {
      return col.name === sourceField || col.label === sourceField;
    });

    if (matchingField) {
      state.field_mappings[sourceTableName][sourceField] = matchingField;
    }
  });
}

function onTableSelected(sourceTableName, targetTable) {
  state.table_mappings[sourceTableName] = targetTable;
  autoFillFieldMappings(sourceTableName);
}

function onFieldSelected(sourceTableName, sourceField, selectedColumn) {
  if (!state.field_mappings[sourceTableName]) {
    state.field_mappings[sourceTableName] = {};
  }
  const targetField = {
    name: selectedColumn.field?.split('→')[1] || selectedColumn.label,
    label: selectedColumn.label,
    type: selectedColumn.field_type,
  };
  state.field_mappings[sourceTableName][sourceField] = targetField;
}

function getFieldDisplayLabel(table, fieldName) {
  const fieldDetail = table.field_details?.[fieldName];
  if (!fieldDetail)
    return fieldName;

  const pathStr = fieldDetail.path?.length ? `${fieldDetail.path.join(' → ')} → ` : '';
  return `${pathStr}${fieldDetail.label || fieldDetail.name}`;
}

async function onAssetChange() {
  if (!state.form_data.selected_asset || state.form_data.selected_asset.uid === route.params.asset_id)
    return;
  extractTablesAndFields();
  await fetchTargetSchemas();
}

// Used when we are duplicating a dashboard to a different asset. This function converts the old tables to their newly mapped tables
// Transform dashboard with new mappings
function transformDashboard() {
  const cloned_dashboard = cloneDeep(selected_dashboard_details.value);

  // Helper to replace table and field references in a string
  const replaceFieldReferences = (text) => {
    if (!text || typeof text !== 'string')
      return text;

    let result = text;

    // Replace all field references in the format [table→field]
    Object.keys(state.table_mappings).forEach((sourceTable) => {
      const targetTable = state.table_mappings[sourceTable];
      if (!targetTable)
        return;

      const sourceFieldMappings = state.field_mappings[sourceTable] || {};
      Object.keys(sourceFieldMappings).forEach((sourceField) => {
        const targetField = sourceFieldMappings[sourceField];
        if (!targetField)
          return;

        // Replace in expressions with brackets: [table→field]
        const sourceRef = `[${sourceTable}→${sourceField}]`;
        const targetRef = `[${targetTable.name}→${targetField.name}]`;
        result = result.split(sourceRef).join(targetRef);

        // Replace without brackets: table→field
        const sourceRefPlain = `${sourceTable}→${sourceField}`;
        const targetRefPlain = `${targetTable.name}→${targetField.name}`;
        result = result.split(sourceRefPlain).join(targetRefPlain);
      });
    });

    return result;
  };

  const cloned_widgets = Array.isArray(cloned_dashboard.widgets) ? cloned_dashboard.widgets : [];
  cloned_widgets.forEach((widget) => {
    const stages = widget.config?.query?.stages || [];

    if (widget.config?.query?.selected_database) {
      widget.config.query.selected_database = `${auth_store.current_organization.uid}__${state.form_data.selected_asset.uid}`;
    }

    stages.forEach((stage) => {
      // Transform main table
      if (stage.table?.name && state.table_mappings[stage.table.name]) {
        const targetTable = state.table_mappings[stage.table.name];
        stage.table.name = targetTable.name;
        stage.table.label = targetTable.label;
        stage.table.alias = targetTable.name;
      }
      else if (typeof stage.table === 'string' && state.table_mappings[stage.table]) {
        const targetTable = state.table_mappings[stage.table];
        stage.table = targetTable.name;
      }

      // Transform joins
      stage.joins?.forEach((join) => {
        if (join.table?.name && state.table_mappings[join.table.name]) {
          const targetTable = state.table_mappings[join.table.name];
          join.table.name = targetTable.name;
          join.table.label = targetTable.label;
          join.table.alias = targetTable.name;
        }
        else if (typeof join.table === 'string' && state.table_mappings[join.table]) {
          const targetTable = state.table_mappings[join.table];
          join.table = targetTable.name;
        }

        // Transform join conditions
        join.on?.forEach((condition) => {
          // Handle left condition
          if (condition.left?.field) {
            condition.left.field = replaceFieldReferences(condition.left.field);
          }
          else if (typeof condition.left === 'string') {
            const originalLeft = condition.left;
            condition.left = replaceFieldReferences(condition.left);

            // Update left_label to match the mapped field
            const parts = originalLeft.split('→');
            if (parts.length === 2) {
              const [sourceTable, sourceField] = parts;
              const targetField = state.field_mappings[sourceTable]?.[sourceField];
              if (targetField && condition.left_label) {
                condition.left_label = targetField.label;
              }
            }
          }

          // Handle right condition
          if (condition.right?.field) {
            condition.right.field = replaceFieldReferences(condition.right.field);
          }
          else if (typeof condition.right === 'string') {
            const originalRight = condition.right;
            condition.right = replaceFieldReferences(condition.right);

            // Update right_label to match the mapped field
            const parts = originalRight.split('→');
            if (parts.length === 2) {
              const [sourceTable, sourceField] = parts;
              const targetField = state.field_mappings[sourceTable]?.[sourceField];
              if (targetField && condition.right_label) {
                condition.right_label = targetField.label;
              }
            }
          }

          // Update table references in labels
          if (condition.left_table && state.table_mappings[condition.left_table]) {
            condition.left_table = state.table_mappings[condition.left_table].name;
          }
          if (condition.right_table && state.table_mappings[condition.right_table]) {
            condition.right_table = state.table_mappings[condition.right_table].name;
          }
        });

        // Transform expression-based join condition
        if (join.condition && typeof join.condition === 'string') {
          join.condition = replaceFieldReferences(join.condition);
        }
      });

      // Transform columns
      stage.columns?.forEach((column) => {
        let sourceTableName = null;
        let sourceFieldName = null;

        // Extract source table and field info
        if (column.field) {
          const parts = column.field.split('→');
          if (parts.length === 2) {
            sourceTableName = parts[0];
            sourceFieldName = parts[1];
          }
        }
        else if (column.table_name) {
          sourceTableName = column.table_name;
          sourceFieldName = column.label || column.name;
        }

        // Get target mappings
        const targetTable = sourceTableName ? state.table_mappings[sourceTableName] : null;
        const targetField = (sourceTableName && sourceFieldName)
          ? state.field_mappings[sourceTableName]?.[sourceFieldName]
          : null;

        // Transform direct field references
        if (column.field) {
          column.field = replaceFieldReferences(column.field);

          // Update table_name from transformed field
          const parts = column.field.split('→');
          if (parts.length === 2) {
            column.table_name = parts[0];
          }
        }

        // Update label to reflect target field
        if (targetField && !column.is_aggregation) {
          column.label = targetField.label;
        }

        // Transform fields array (used in aggregations)
        if (column.fields && Array.isArray(column.fields)) {
          column.fields = column.fields.map(field => replaceFieldReferences(field));
        }

        // Transform expressions
        if (column.expr) {
          column.expr = replaceFieldReferences(column.expr);
        }

        // Handle table_name for non-field columns
        if (column.table_name && !column.field && targetTable && targetField) {
          column.table_name = targetTable.name;
          column.field = `${targetTable.name}→${targetField.name}`;
        }
      });

      // Transform filters
      stage.filters?.forEach((filter) => {
        // Transform field reference
        if (filter.field) {
          filter.field = replaceFieldReferences(filter.field);

          // Extract table_name from transformed field
          const parts = filter.field.split('→');
          if (parts.length === 2) {
            filter.table_name = parts[0];
          }
        }

        // Transform expression-based filters
        if (filter.expr) {
          filter.expr = replaceFieldReferences(filter.expr);
        }

        // Legacy table_name handling
        if (filter.table_name && !filter.field && state.table_mappings[filter.table_name]) {
          const targetTable = state.table_mappings[filter.table_name];
          const sourceField = filter.name;
          const targetField = state.field_mappings[filter.table_name]?.[sourceField];

          if (targetField) {
            filter.table_name = targetTable.name;
            filter.field = `${targetTable.name}→${targetField.name}`;
            filter.name = targetField.name;
          }
        }
      });
    });
  });

  // Transform global filters
  cloned_dashboard.meta?.global_filters?.forEach((filter) => {
    if (filter.general?.dataset && state.table_mappings[filter.general.dataset]) {
      const targetTable = state.table_mappings[filter.general.dataset];
      const sourceField = filter.general.column_name;
      const targetField = state.field_mappings[filter.general.dataset]?.[sourceField];

      if (targetField) {
        filter.general.dataset = targetTable.name;
        filter.general.column = `${targetTable.name}→${targetField.name}`;
        filter.general.column_name = targetField.name;
      }
    }
  });

  return cloned_dashboard;
}

async function onSave() {
  if (is_save_disabled.value)
    return;

  state.is_loading = true;
  try {
    if (!state.form_data.selected_asset || state.form_data.selected_asset.uid === route.params.asset_id) {
      const new_dashboard = {
        name: `${state.form_data.name || selected_dashboard_details.value.name || 'Untitled'}`,
        default: false,
        report_data: cloneDeep(selected_dashboard_details.value?.widgets) || [],
        meta: selected_dashboard_details.value?.meta || {},
        organization: auth_store?.current_organization.uid,
        public: selected_dashboard_details.value.public,
        members: selected_dashboard_details.value.members,
        standard: selected_dashboard_details.value?.standard || false,
      };
      await bi_store.createDashboard(new_dashboard);
      $toast({
        title: $t('Successful!'),
        text: `${$t('Dashboard duplicated successfully')}`,
        type: 'success',
      });
    }
    else {
      const transformedDashboard = transformDashboard();
      const new_dashboard = {
        name: `${state.form_data.name || 'Untitled'}`,
        default: false,
        report_data: transformedDashboard.widgets || [],
        meta: transformedDashboard.meta || {},
        organization: auth_store.current_organization.uid,
        public: true,
        members: {},
        asset: state.form_data.selected_asset.uid,
      };

      const target_bi_store = useBiStore(`bi_${auth_store.current_organization.uid}__${state.form_data.selected_asset.uid}`);
      const created_dashboard = await target_bi_store.createDashboard(new_dashboard);

      router.push({ name: 'bi-asset-dashboard', params: { asset_id: created_dashboard[0].asset }, query: { dashboard: created_dashboard[0].uid } });

      $toast({
        title: $t('Successful!'),
        text: `${$t('Dashboard duplicated successfully')}`,
        type: 'success',
      });
    }
  }
  catch (error) {
    console.error('Failed to duplicate dashboard:', error);
    $toast({
      title: $t('Error'),
      text: $t('Failed to duplicate dashboard'),
      type: 'error',
    });
  }
  finally {
    state.is_loading = false;
    emit('close');
  }
}

function formatTables(tables) {
  tables = tables.map(table =>
    (
      {
        label: table.label || table.name,
        name: table.name,
        path: table.path,
        columns: table.columns.map(column => ({
          ...column,
          table_name: table.name,
          field: bi_query_builder.constructFieldName({ label: column.name, table_name: table.name }),
          label: column.label || column.name,
          alias: bi_query_builder.constructColumnAlias({ table_name: table.label, path: column.path, column_name: column.label || column.name }),
        })),
      }
    ));
  return tables;
}
</script>

<template>
  <HawkModalContainer id="bi-duplicate-dashboard-to-asset-modal" :content_class="state.used_tables.length ? '!w-[calc(60vw)]' : ''">
    <Vueform
      v-model="state.form_data"
      size="sm"
      :display-errors="false"
      :columns="{ container: 12, label: 4, wrapper: 12 }"
    >
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            <div class="flex flex-col justify-start">
              {{ $t('Duplicate dashboard') }}
            </div>
          </template>
        </HawkModalHeader>

        <HawkModalContent>
          <TextElement
            name="name"
            :label="$t('Name')"
            :default="`${selected_dashboard_details.name || 'Untitled'} - ${$t('Copy')}`"
            rules="required"
            class="mb-6"
          />
          <SelectElement
            name="selected_asset"
            :label="$t('Asset')"
            :items="bi_enabled_assets"
            :default="bi_enabled_assets.find(asset => asset.uid === route.params.asset_id) || null"
            :native="false"
            :can-clear="false"
            :can-deselect="false"
            :search="true"
            :object="true"
            :disabled="is_standard_dashboard"
            label-prop="name"
            value-prop="uid"
            :placeholder="$t('Choose an asset')"
            append-to="#bi-duplicate-dashboard-to-asset-modal"
            @change="onAssetChange"
          >
            <template #single-label="{ value }">
              <div class="flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3">
                <HawkAvatar
                  class="mr-2"
                  :img_src="value?.cover_image?.url"
                  :src="value?.cover_image?.url"
                  :name="value?.name"
                  :background_color="stringToNumber(value?.name)"
                />
                <p
                  v-tippy="value.name"
                  class="truncate max-w-[175px]"
                >
                  {{ value.name }}
                </p>
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex justify-between w-full">
                <div class="flex items-center">
                  <HawkAvatar
                    class="mr-2"
                    :img_src="option?.cover_image?.url"
                    :src="option?.cover_image?.url"
                    :name="option?.name"
                    :background_color="stringToNumber(option?.name)"
                  />
                  <p
                    v-tippy="option.name"
                    class="font-medium truncate max-w-[175px]"
                  >
                    {{ option.name }}
                  </p>
                </div>
                <div class="flex items-center">
                  <IconHawkCheck v-if="option.uid === asset" class="text-blue-500 h-4 w-4" />
                </div>
              </div>
            </template>
          </SelectElement>

          <div v-if="state.form_data.selected_asset?.uid && state.form_data.selected_asset.uid !== route.params.asset_id" :class="state.is_fetching_schemas || state.used_tables.length ? 'mt-6' : ''">
            <HawkLoader v-if="state.is_fetching_schemas" />

            <div v-else-if="state.used_tables.length">
              <div class="text-md font-semibold text-gray-900">
                {{ $t('Map datasets and their fields') }}
              </div>

              <div class="space-y-3 pr-2 mt-3">
                <div v-for="table in state.used_tables" :key="table.name" class="rounded-lg p-4 space-y-4">
                  <div class="grid grid-cols-9 gap-4 items-center">
                    <div class="col-span-4">
                      <div class="text-xs font-normal text-gray-600 flex items-center gap-1 flex-wrap">
                        <template v-for="(segment, index) in table.path" :key="index">
                          <span>{{ segment }}</span>
                          <IconHawkChevronRight v-if="index < table.path.length - 1" class="size-3 flex-shrink-0" />
                        </template>
                      </div>
                      <div class="text-sm font-semibold text-gray-900">
                        {{ table.label }}
                      </div>
                    </div>

                    <IconHawkArrowRight class="col-span-1 text-gray-600" />

                    <div class="col-span-4">
                      <BiQueryBuilderTablePicker
                        :hierarchical-schemas="targetHierarchicalSchemas"
                        :model-value="state.table_mappings[table.name]"
                        @update:model-value="onTableSelected(table.name, $event._schema || $event)"
                      >
                        <template #trigger="{ open }">
                          <div class="flex items-center justify-between cursor-pointer">
                            <div v-if="state.table_mappings[table.name]">
                              <div class="text-xs font-normal text-gray-600 flex items-center gap-1 flex-wrap">
                                <template v-for="(segment, index) in state.table_mappings[table.name].path" :key="index">
                                  <span>{{ segment }}</span>
                                  <IconHawkChevronRight v-if="index < state.table_mappings[table.name].path.length - 1" class="size-3 flex-shrink-0" />
                                </template>
                              </div>
                              <div class="text-sm font-semibold text-gray-900">
                                {{ state.table_mappings[table.name].label }}
                              </div>
                            </div>
                            <div v-else class="text-sm text-gray-500">
                              {{ $t('Select a dataset') }}
                            </div>
                            <IconHawkChevronDown v-if="!open" class="size-4 flex-shrink-0 ml-1" />
                            <IconHawkChevronUp v-else class="size-4 flex-shrink-0 ml-1" />
                            <IconHawkAlertTriangle
                              v-if="!state.table_mappings[table.name]"
                              class="size-4 flex-shrink-0 ml-1 text-warning-500"
                            />
                          </div>
                        </template>
                      </BiQueryBuilderTablePicker>
                    </div>
                  </div>

                  <div v-if="state.table_mappings[table.name] && table.fields.length > 0" class="space-y-3">
                    <div v-for="field in table.fields" :key="field" class="grid grid-cols-9 gap-4 items-center">
                      <div class="col-span-4 text-sm font-normal text-gray-900 ml-3">
                        {{ getFieldDisplayLabel(table, field) }}
                      </div>
                      <div class="col-span-1" />

                      <HawkMenu
                        :items="[]"
                        class="col-span-4 w-fit ml-3"
                        position="fixed"
                        additional_trigger_classes="!px-0 !py-0 !ring-0"
                        additional_dropdown_classes="transition duration-150 ease-in-out"
                      >
                        <template #trigger="{ open }">
                          <div class="flex items-center justify-between cursor-pointer">
                            <div v-if="state.field_mappings[table.name]?.[field]" class="text-sm font-normal text-gray-900">
                              {{ state.field_mappings[table.name][field].label }}
                            </div>
                            <div v-else class="text-sm text-gray-500">
                              {{ $t('Select a field') }}
                            </div>
                            <IconHawkChevronDown v-if="!open" class="size-4 flex-shrink-0 ml-1" />
                            <IconHawkChevronUp v-else class="size-4 flex-shrink-0 ml-1" />
                            <IconHawkAlertTriangle
                              v-if="!state.field_mappings[table.name]?.[field]"
                              class="size-4 flex-shrink-0 ml-1 text-warning-500"
                            />
                          </div>
                        </template>

                        <template #content="{ close }">
                          <BiQueryBuilderColumnPicker
                            :tables="formatTables([state.table_mappings[table.name]])"
                            :has-functions="false"
                            :show-stage-header="false"
                            :supported-types="[
                              'string',
                              'text',
                              'text_array',
                              'numeric',
                              'integer',
                              'decimal',
                              'float',
                              'date',
                              'time',
                              'timestamp',
                              'boolean',
                            ]"
                            @selected="onFieldSelected(table.name, field, $event); close()"
                          />
                        </template>
                      </HawkMenu>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="!is_mapping_complete"
                class="text-sm font-semibold flex items-center gap-2 p-4 border border-warning-300 rounded-md text-warning-700 bg-warning-25"
              >
                <IconHawkAlertTriangle />
                {{ $t('You need to map all dataset and fields to duplicate the dashboard.') }}
              </div>
            </div>
          </div>
        </HawkModalContent>

        <HawkModalFooter>
          <template #right>
            <div class="flex justify-end w-full">
              <div class="flex gap-2">
                <HawkButton type="outlined" @click="emit('close')">
                  {{ $t('Cancel') }}
                </HawkButton>
                <HawkButton
                  :loading="state.is_loading"
                  :disabled="is_save_disabled"
                  @click="onSave"
                >
                  {{ $t('Duplicate') }}
                </HawkButton>
              </div>
            </div>
          </template>
        </HawkModalFooter>
      </div>
    </Vueform>
  </HawkModalContainer>
</template>
